import React, { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Card,
  Box,
  Snackbar,
  Alert,
  Typography,
  MenuItem,
  IconButton,
} from "@mui/material";
import { IconUpload, IconX, IconFileDescription } from "@tabler/icons-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDAutocomplete from "components/MDAutocomplete";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDTextField from "components/MDTextField";
import MDBox from "components/MDBox";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { generatePdfThumbnail } from "utils/pdfPreview";
import { compressImage } from "utils/imageCompression";

export default function TicketAdd() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const priorities = [
    { title: "Urgent", value: "Urgent" },
    { title: "Normal", value: "Normal" },
    { title: "Medium", value: "Medium" },
  ];

  const [state, setState] = useState({
    open: false,
    severity: "success",
    vertical: "top",
    horizontal: "right",
    message: "",
  });

  const [preview, setPreview] = useState([]);

  const handleClose = () => setState({ ...state, open: false });

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  const formik = useFormik({
    initialValues: {
      txtPrioritization: "",
      txtSubject: "",
      txtContent: "",
      documents: [],
    },
    validationSchema: Yup.object({
      txtPrioritization: Yup.string().required("Prioritization is required"),
      txtSubject: Yup.string().required("Subject is required"),
      txtContent: Yup.string()
        .max(500, "Maximum 500 characters allowed")
        .required("Description is required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // 🔹 Get logged-in user info on form submit
        const meRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/me`,
          {},
          { withCredentials: true }
        );
        const meData = meRes.data;

        console.log("SUBMITTING WITH:", values, meData);

        const formData = new FormData();
        formData.append("prioritization", values.txtPrioritization);
        formData.append("ticket_subject", values.txtSubject);
        formData.append("ticket_description", values.txtContent);

        if (values.documents && values.documents.length > 0) {
          const compressedFiles = await Promise.all(
            values.documents.map(file => compressImage(file))
          );
          compressedFiles.forEach((file) => {
            formData.append("ticket_attachment", file);
          });
        }

        formData.append("created_user", meData.user_id);
        formData.append("created_user_name", meData.user_name);
        formData.append("created_user_district", meData.district);
        formData.append("status", "Open");

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/tickets/add`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );

        if (response.data.status === 200) {
          setState({
            ...state,
            open: true,
            severity: "success",
            message: response.data.message,
          });

          formik.resetForm();
          setPreview(null);

          setTimeout(() => {
            navigate("/tickets-list");
          }, 2000);
        }
      } catch (err) {
        console.error("Create Ticket Error:", err);
        if (err.response) {
          console.error("Response:", err.response.data);
        }
        setState({
          ...state,
          open: true,
          severity: "error",
          message: "Failed to create ticket",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleFileChange = async (event) => {
    const files = Array.from(event.currentTarget.files);
    if (files.length > 0) {
      const newFiles = [...formik.values.documents, ...files];
      formik.setFieldValue("documents", newFiles);

      // Update previews
      const previewPromises = newFiles.map(async (file) => {
        if (file.type.startsWith("image/")) {
          return { name: file.name, type: file.type, url: URL.createObjectURL(file) };
        } else if (file.type === "application/pdf") {
          try {
            const thumbnail = await generatePdfThumbnail(file);
            return { name: file.name, type: file.type, url: thumbnail };
          } catch (e) {
            return { name: file.name, type: file.type, url: null };
          }
        }
        return { name: file.name, type: file.type, url: null };
      });

      const newPreviews = await Promise.all(previewPromises);
      setPreview(newPreviews);
    }
  };
  
  const handleRemoveFile = (index) => {
    const newFiles = [...formik.values.documents];
    newFiles.splice(index, 1);
    formik.setFieldValue("documents", newFiles);

    // Update previews by removing the corresponding entry
    const newPreviews = [...preview];
    newPreviews.splice(index, 1);
    setPreview(newPreviews);
  };

  if (meData && (meData.role_id === 1 || meData.role_id === 2)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
      >
        <Typography variant="h4" color="error">
          403 - Unauthorized
        </Typography>
        <Typography>You are not authorized to access this page.</Typography>
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled">
          {state.message}
        </Alert>
      </Snackbar>

      <Card id="delete-account">
        {/* <form onSubmit={formik.handleSubmit}> */}
        <MDBox p={2}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 12 }}>
              <MDAutocomplete
                label="Prioritization"
                name="txtPrioritization"
                options={priorities}
                formik={formik}
                required={true}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 12 }}>
              <MDTextField
                error={formik.errors.txtSubject}
                handleChange={formik.handleChange}
                name="txtSubject"
                value={formik.values.txtSubject}
                placeholder="Subject"
                displayName="Subject"
                required={true}
                formik={formik}
                inputType="onlyString"
                maxLength={50}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                label="Description"
                name="txtContent"
                value={formik.values.txtContent}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.txtContent && formik.errors.txtContent)}
                helperText={
                  formik.touched.txtContent && formik.errors.txtContent
                    ? formik.errors.txtContent
                    : `${formik.values.txtContent.length}/500`
                }
                multiline
                rows={5}
                inputProps={{ maxLength: 500 }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    marginRight: 0,
                    display: "block",
                    fontSize: "0.75rem", // optional
                  },
                }}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 12 }}>
              <Typography sx={{ fontWeight: 500, mb: 1 }}>
                Attach any supporting documents or images
              </Typography>

              <Box
                p={2}
                border="1px dashed"
                borderColor="primary.main"
                borderRadius={2}
                bgcolor="#f9f9f9"
                sx={{
                  maxWidth: 450,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.5,
                  position: "relative",
                }}
              >
                <input
                  type="file"
                  id="documents"
                  name="documents"
                  hidden
                  multiple
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={handleFileChange}
                  onBlur={formik.handleBlur}
                />
                <label htmlFor="documents">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<IconUpload size={18} />}
                    component="span"
                    sx={{
                      borderColor: "black",
                      fontWeight: 500,
                      color: "black !important",
                      "& .MuiButton-startIcon": {
                        color: "black !important",
                      },
                      "& .MuiButton-label": {
                        color: "black !important",
                      },
                      "&:hover": {
                        backgroundColor: "black !important",
                        color: "white !important",
                        borderColor: "black !important",
                        "& .MuiSvgIcon-root": {
                          color: "white !important",
                        },
                        "& .MuiButton-startIcon": {
                          color: "white !important", // <-- icon turns white on hover
                          "& svg": {
                            color: "white !important",
                          },
                        },
                      },
                    }}
                  >
                    {formik.values.documents && formik.values.documents.length > 0 ? "Add More Files" : "Upload File"}
                  </Button>
                </label>
                {formik.values.documents && formik.values.documents.length > 0 && (
                  <Box sx={{ mt: 2, width: "100%" }}>
                    {formik.values.documents.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mt: 1,
                          p: 1,
                          bgcolor: "#fff",
                          borderRadius: 1,
                          border: "1px solid #eee",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" sx={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {file.name}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleRemoveFile(index)}
                          size="small"
                          sx={{
                            p: 0.5,
                            backgroundColor: "#f5f5f5",
                            borderRadius: "50%",
                            transition: "0.3s ease",
                            "&:hover": {
                              backgroundColor: "red",
                              "& svg": {
                                color: "#fff",
                              },
                            },
                          }}
                        >
                          <IconX size={18} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {preview && preview.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                    {preview.map((p, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        {p.url ? (
                          <img
                            src={p.url}
                            alt={`Preview ${index}`}
                            style={{
                              width: 80,
                              height: 80,
                              borderRadius: 6,
                              objectFit: "cover",
                              border: "1px solid #eee",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 6,
                              bgcolor: "#f0f0f0",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid #eee",
                            }}
                          >
                            <IconFileDescription size={40} color="#888" />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.6rem",
                                textAlign: "center",
                                px: 0.5,
                                wordBreak: "break-all",
                                color: "#666"
                              }}
                            >
                              {p.name.split(".").pop().toUpperCase()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </MDBox>

        {/* Submit */}
        <MDBox p={2}>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Grid item xs={12} md={12} textAlign="right">
              <MDButton
                // color="#2f2f34 !important"
                onClick={formik.handleSubmit}
                disabled={loading}
                sx={{
                  color: "white !important",
                  backgroundColor: "#16610E",
                  "&:hover": {
                    backgroundColor: "#1f1f22", // optional: darker on hover
                  },
                }}
              >
                Create Ticket
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
        {/* </form> */}
      </Card>
    </DashboardLayout>
  );
}
